import { useFormik } from "formik";
import { createUser } from "../core/_request";
import { useToast } from "../../../modules/toast/Toast";
import { useNavigate } from "react-router-dom";
import { useQueryResponse } from "../../merchant-management/core/QueryResponseProvider";
import { Merchant } from "../../merchant-management/core/_model";
import { useQueryRequest } from "../../merchant-management/core/QueryRequestProvider";
import { useEffect, useState } from "react";
import { useDebounce } from "../../../../_mcp/helpers";
import { useAuth } from "../../../modules/auth";
import clsx from "clsx";
import {
  createUserValidationSchema,
  userInitialValues,
  UserFormValues,
} from "./userValidation";
import {
  prepareUserPayload,
  getUserFieldError,
  validateUserForm,
} from "./userValidationHelpers";
import { encryptAES256CBC } from "../../../modules/auth/utils/encryption";

const generateRandomToken = (): string => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const generateSecurePIN = (): string => {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const specialChar = "!@#$%^&*()_+-=[]{}|;:,.<>?";
  const allChars = uppercase + lowercase + numbers + specialChar;

  let result = "";

  // Ensure at least one character from each category (4 categories)
  const categories = [uppercase, lowercase, numbers, specialChar];
  const randomValues = new Uint32Array(8);
  crypto.getRandomValues(randomValues);

  // Pick one character from each category to ensure strong password
  for (let i = 0; i < categories.length; i++) {
    const category = categories[i];
    result += category.charAt(randomValues[i] % category.length);
  }

  // Fill the rest (minimum 6 characters required by backend)
  // We already have 4 from categories, add 2 more random chars
  for (let i = 4; i < 8; i++) {
    result += allChars.charAt(randomValues[i] % allChars.length);
  }

  // Shuffle to randomize position
  const shuffled = result.split("");
  const shuffleValues = new Uint32Array(8);
  crypto.getRandomValues(shuffleValues);

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = shuffleValues[i] % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled.join("");
};

const CreateUser = () => {
  const { currentUser } = useAuth();

  // Determine user role and redirect accordingly
  const getUserRole = () => {
    if (!currentUser?.auth?.role) return null;
    return currentUser.auth.role;
  };

  const role = getUserRole();
  const isInternal = role?.startsWith("INTERNAL");

  const dataMerchant: Merchant[] | any = useQueryResponse();
  const { updateState } = useQueryRequest();
  const merchantData = dataMerchant.response
    ? dataMerchant.response.detailData.docs
    : [];
  const { notify } = useToast();

  const navigate = useNavigate();

  // Merchant search state
  const [merchantSearchTerm, setMerchantSearchTerm] = useState<string>("");
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  const debouncedMerchantSearch = useDebounce(merchantSearchTerm, 500);

  // Filter merchants based on search term
  useEffect(() => {
    if (debouncedMerchantSearch) {
      const filtered = merchantData.filter(
        (merchant: Merchant) =>
          merchant.merchantName
            .toLowerCase()
            .includes(debouncedMerchantSearch.toLowerCase()) ||
          merchant.merchantBrandName
            .toLowerCase()
            .includes(debouncedMerchantSearch.toLowerCase()) ||
          merchant._id
            .toLowerCase()
            .includes(debouncedMerchantSearch.toLowerCase())
      );
      setFilteredMerchants(filtered);
    } else {
      setFilteredMerchants(merchantData);
    }
  }, [debouncedMerchantSearch, merchantData]);

  // Update merchant search in query state (only when merchant dropdown is relevant)
  useEffect(() => {
    if (debouncedMerchantSearch !== undefined) {
      updateState({
        search: debouncedMerchantSearch,
        page: 1,
        limit: 10,
      });
    }
  }, [debouncedMerchantSearch, updateState]);

  const formik = useFormik<UserFormValues>({
    initialValues: {
      ...userInitialValues,
      merchantId: isInternal ? null : currentUser?.auth?.merchantId,
      tokenApproval: generateSecurePIN(),
    },
    validationSchema: createUserValidationSchema(!!isInternal),
    validateOnChange: true, // Enable real-time validation
    validateOnBlur: true,
    validateOnMount: false, // Don't validate on initial load
    onSubmit: async (values) => {
      try {
        // Encrypt tokenLogin before preparing payload
        const encryptedTokenLogin = encryptAES256CBC(values.tokenLogin);
        const encryptedTokenApproval = encryptAES256CBC(values.tokenApproval);

        // Create payload with encrypted tokenLogin
        const payload = {
          ...prepareUserPayload(values),
          tokenLogin: encryptedTokenLogin,
          tokenApproval: encryptedTokenApproval,
        };

        const response = await createUser(payload);
        if (response.responseCode === "200") {
          navigate(-1);
          notify("success", "Submit Success");
        } else {
          notify("error", response.responseMessage);
        }
      } catch (err: any) {
        console.error("Create user error:", err); // Debug log
        notify("error", err?.response.data.responseMessage);
      }
    },
  });

  // Clear merchantId when selected role is INTERNAL
  useEffect(() => {
    if (formik.values.role?.startsWith("INTERNAL")) {
      formik.setFieldValue("merchantId", "");
      setMerchantSearchTerm("");
    }
  }, [formik.values.role]);

  const handleMerchantSelect = (merchant: Merchant) => {
    formik.setFieldValue("merchantId", merchant._id);
    setMerchantSearchTerm(merchant.merchantName);
    setShowMerchantDropdown(false);
  };

  const getSelectedMerchantName = () => {
    if (!formik.values.merchantId) return "";
    const merchant = merchantData.find(
      (m: Merchant) => m._id === formik.values.merchantId
    );
    return merchant ? merchant.merchantName : "";
  };

  const selectedRoleIsInternal = formik.values.role?.startsWith("INTERNAL");

  const regenerateTokens = () => {
    formik.setFieldValue("tokenApproval", generateSecurePIN());
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header Section */}
      <div className="container-fluid px-8 mb-6">
        <div className="bg-white rounded-4 shadow-sm p-6 border-0">
          <div className="mb-4">
            <h3 className="fw-bold text-gray-800 mb-2">Create New User</h3>
            <p className="text-muted fs-7 mb-0">
              Add a new user to the system with proper role and permissions
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid px-8 flex-grow-1">
        <form onSubmit={formik.handleSubmit} className="h-100">
          <div className="row g-6">
            {/* User Information Card */}
            <div className="col-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div
                  className="card-header bg-white py-4"
                  style={{ borderBottom: "2px solid #e4e6ef" }}
                >
                  <div className="d-flex align-items-center">
                    <div className="symbol symbol-35px me-3">
                      <div className="symbol-label bg-primary">
                        <i className="fas fa-user text-white"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="card-title mb-0 fw-bold text-gray-800">
                        User Information
                      </h3>
                      <p className="card-subtitle text-muted fs-6 mb-0">
                        Basic user details and account information
                      </p>
                    </div>
                  </div>
                </div>
                <div className="card-body p-10">
                  <div className="row g-6">
                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">
                        Full Name *
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-user text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className={clsx("form-control", {
                            "is-invalid":
                              formik.touched.fullName && formik.errors.fullName,
                          })}
                          placeholder="Enter full name"
                          {...formik.getFieldProps("fullName")}
                        />
                      </div>
                      {getUserFieldError(formik, "fullName") && (
                        <div className="text-danger fs-7 mt-2">
                          {getUserFieldError(formik, "fullName")}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">
                        Username *
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-at text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className={clsx("form-control", {
                            "is-invalid":
                              formik.touched.userName && formik.errors.userName,
                          })}
                          placeholder="Enter username"
                          {...formik.getFieldProps("userName")}
                        />
                      </div>
                      {getUserFieldError(formik, "userName") && (
                        <div className="text-danger fs-7 mt-2">
                          {getUserFieldError(formik, "userName")}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">
                        Email Address *
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-envelope text-gray-600"></i>
                        </span>
                        <input
                          type="email"
                          className={clsx("form-control", {
                            "is-invalid":
                              formik.touched.email && formik.errors.email,
                          })}
                          placeholder="Enter email address"
                          {...formik.getFieldProps("email")}
                        />
                      </div>
                      {getUserFieldError(formik, "email") && (
                        <div className="text-danger fs-7 mt-2">
                          {getUserFieldError(formik, "email")}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">
                        Role *
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-shield-alt text-gray-600"></i>
                        </span>
                        <select
                          className={clsx("form-select", {
                            "is-invalid":
                              formik.touched.role && formik.errors.role,
                          })}
                          {...formik.getFieldProps("role")}
                        >
                          <option value="">Select Role</option>
                          {isInternal && (
                            <>
                              <option value="INTERNAL_ADMIN">
                                INTERNAL ADMIN
                              </option>
                              <option value="INTERNAL_MAKER">
                                INTERNAL MAKER
                              </option>
                              <option value="INTERNAL_APPROVAL">
                                INTERNAL APPROVAL
                              </option>
                            </>
                          )}
                          <option value="MERCHANT_ADMIN">MERCHANT ADMIN</option>
                          <option value="MERCHANT_MAKER">MERCHANT MAKER</option>
                          <option value="MERCHANT_APPROVAL">
                            MERCHANT APPROVAL
                          </option>
                        </select>
                      </div>
                      {getUserFieldError(formik, "role") && (
                        <div className="text-danger fs-7 mt-2">
                          {getUserFieldError(formik, "role")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Merchant Assignment Card - Only for INTERNAL users assigning MERCHANT roles */}
            {isInternal && !selectedRoleIsInternal && (
              <div className="col-12">
                <div className="card shadow-sm border-0 rounded-4">
                  <div
                    className="card-header bg-white py-4"
                    style={{ borderBottom: "2px solid #e4e6ef" }}
                  >
                    <div className="d-flex align-items-center">
                      <div className="symbol symbol-35px me-3">
                        <div className="symbol-label bg-primary">
                          <i className="fas fa-building text-white"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="card-title mb-0 fw-bold text-gray-800">
                          Merchant Assignment
                        </h3>
                        <p className="card-subtitle text-muted fs-6 mb-0">
                          Assign user to a specific merchant
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-10">
                    <div className="row g-6">
                      <div className="col-12">
                        <label className="form-label text-gray-700 fw-semibold mb-3">
                          Merchant *
                        </label>
                        <div className="position-relative">
                          <div className="input-group input-group-lg">
                            <span className="input-group-text">
                              <i className="fas fa-search text-gray-600"></i>
                            </span>
                            <input
                              type="text"
                              className={clsx("form-control", {
                                "is-invalid":
                                  formik.touched.merchantId &&
                                  formik.errors.merchantId,
                              })}
                              placeholder="Search for a merchant..."
                              value={merchantSearchTerm}
                              onChange={(e) => {
                                setMerchantSearchTerm(e.target.value);
                                setShowMerchantDropdown(true);
                                if (!e.target.value) {
                                  formik.setFieldValue("merchantId", "");
                                }
                              }}
                              onFocus={() => setShowMerchantDropdown(true)}
                            />
                          </div>
                          {showMerchantDropdown && (
                            <div
                              className="position-absolute w-100 bg-white border rounded-3 shadow-lg mt-2"
                              style={{
                                zIndex: 1000,
                                maxHeight: "250px",
                                overflowY: "auto",
                              }}
                            >
                              {filteredMerchants.length > 0 ? (
                                filteredMerchants.map((merchant: Merchant) => (
                                  <div
                                    key={merchant._id}
                                    className="p-3 cursor-pointer border-bottom border-light hover-bg-light-primary"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      handleMerchantSelect(merchant)
                                    }
                                  >
                                    <div className="d-flex align-items-start">
                                      <div className="symbol symbol-40px me-3">
                                        <div className="symbol-label bg-light-primary">
                                          <i className="fas fa-building text-primary"></i>
                                        </div>
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="fw-bold text-gray-800 fs-6">
                                          {merchant.merchantName}
                                        </div>
                                        <div className="text-muted fs-7">
                                          {merchant.merchantBrandName}
                                        </div>
                                        <div className="text-muted fs-8">
                                          ID: {merchant._id}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-4 text-center text-muted">
                                  <i className="fas fa-search fs-2 mb-2"></i>
                                  <div>No merchants found</div>
                                </div>
                              )}
                            </div>
                          )}
                          {formik.values.merchantId && (
                            <div className="mt-3 p-3 bg-light-success rounded-3">
                              <div className="d-flex align-items-center">
                                <div className="symbol symbol-30px me-3">
                                  <div className="symbol-label bg-success">
                                    <i className="fas fa-check text-white fs-7"></i>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-800 fw-semibold fs-7">
                                    Selected Merchant:
                                  </div>
                                  <div className="text-success fw-bold">
                                    {getSelectedMerchantName()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {getUserFieldError(formik, "merchantId") && (
                          <div className="text-danger fs-7 mt-2">
                            {getUserFieldError(formik, "merchantId")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Configuration Card */}
            <div className="col-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div
                  className="card-header bg-white py-4"
                  style={{ borderBottom: "2px solid #e4e6ef" }}
                >
                  <div className="d-flex align-items-center">
                    <div className="symbol symbol-35px me-3">
                      <div className="symbol-label bg-primary">
                        <i className="fas fa-key text-white"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="card-title mb-0 fw-bold text-gray-800">
                        Security Configuration
                      </h3>
                      <p className="card-subtitle text-muted fs-6 mb-0">
                        Authentication tokens and security settings
                      </p>
                    </div>
                  </div>
                </div>
                <div className="card-body p-10">
                  <div className="row g-6">
                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">
                        Token Login *
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-fingerprint text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className={clsx("form-control", {
                            "is-invalid":
                              formik.touched.tokenLogin &&
                              formik.errors.tokenLogin,
                          })}
                          placeholder="Enter login token"
                          {...formik.getFieldProps("tokenLogin")}
                        />
                      </div>
                      {getUserFieldError(formik, "tokenLogin") && (
                        <div className="text-danger fs-7 mt-2">
                          {getUserFieldError(formik, "tokenLogin")}
                        </div>
                      )}
                      <div className="form-text text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Must be at least 6 characters, contain 1 uppercase
                        letter and 1 special character
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">
                        PIN *
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-fingerprint text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control bg-light-secondary"
                          value={formik.values.tokenApproval}
                          readOnly
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() =>
                            formik.setFieldValue(
                              "tokenApproval",
                              generateSecurePIN()
                            )
                          }
                        >
                          <i className="fas fa-sync-alt"></i>
                        </button>
                      </div>
                      <div className="form-text text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Auto-generated secure PIN for transaction approvals and
                        sensitive operations
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-4 shadow-sm border-0 p-6 mt-6">
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                <div className="fs-7">Ready to create user account?</div>
                <div className="fs-8">
                  All information will be validated and saved
                </div>
              </div>
              <div className="d-flex gap-3">
                <button
                  type="button"
                  className="btn btn-light btn-lg px-8"
                  onClick={() => {
                    formik.resetForm();
                    formik.setFieldValue("tokenApproval", generateSecurePIN());
                    formik.setFieldValue(
                      "merchantId",
                      isInternal ? null : currentUser?.auth?.merchantId
                    );
                  }}
                >
                  <i className="fas fa-undo me-2"></i>
                  Reset Form
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-lg px-8 shadow-primary d-flex align-items-center"
                  disabled={formik.isSubmitting}
                  onClick={async () => {
                    if (await validateUserForm(formik)) {
                      formik.handleSubmit();
                    }
                  }}
                >
                  {formik.isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Creating User...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-plus me-2"></i>
                      Create User Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUser;
